import { useState } from 'react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Typography,
  makeStyles,
} from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi } from '@backstage/core-plugin-api';
import { auth0OnboardingApiRef } from '../api';

const useStyles = makeStyles(theme => ({
  meta: { color: theme.palette.text.secondary, marginBottom: theme.spacing(2) },
  hint: {
    color: theme.palette.text.hint,
    fontSize: '0.75rem',
    marginTop: theme.spacing(1),
  },
}));

export const StarterCodeCard = () => {
  const { entity } = useEntity();
  const api = useApi(auth0OnboardingApiRef);
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const annotations = entity.metadata.annotations ?? {};
  const frontend = annotations['arkance.world/frontend'] ?? '—';
  const backend = annotations['arkance.world/backend'] ?? '—';
  const createdAt = annotations['arkance.world/created-at'];
  const repoUrl = annotations['arkance.world/repo-url'];
  // No web frontend, API or jobs: nothing was generated to push or download.
  const hasSource = annotations['arkance.world/app-type'] !== 'no_compute';
  const appName = entity.metadata.name;

  const handleDownload = async () => {
    setLoading(true);
    setError(undefined);
    try {
      const url = await api.getArtifactUrl(appName);
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        setError('No starter code ZIP is stored for this app.');
      }
    } catch {
      setError('Download failed. The artifact may not be available yet.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenRepo = () => {
    window.open(repoUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <Card>
      <CardHeader title="Starter Code" />
      <CardContent>
        {hasSource && (
          <Typography variant="body2" className={classes.meta}>
            {frontend} + {backend}
            {createdAt && ` · Created ${new Date(createdAt).toLocaleDateString()}`}
          </Typography>
        )}
        {!hasSource && !repoUrl && (
          <Typography variant="body2" className={classes.meta}>
            This app has no web frontend, API or jobs, so no starter code was
            generated.
          </Typography>
        )}
        {repoUrl && (
          <>
            <Button
              variant="contained"
              color="primary"
              startIcon={<OpenInNewIcon />}
              onClick={handleOpenRepo}
              fullWidth
            >
              Open in Azure DevOps
            </Button>
            <Typography className={classes.hint}>
              Source of truth lives in the Azure DevOps repository
            </Typography>
          </>
        )}
        {hasSource && !repoUrl && (
          <>
            <Button
              variant="contained"
              color="primary"
              startIcon={<GetAppIcon />}
              onClick={handleDownload}
              disabled={loading}
              fullWidth
            >
              {loading ? 'Preparing download…' : 'Download ZIP'}
            </Button>
            {error && (
              <Typography color="error" className={classes.hint}>
                {error}
              </Typography>
            )}
            <Typography className={classes.hint}>
              Same code as when the app was created
            </Typography>
          </>
        )}
      </CardContent>
    </Card>
  );
};
